import { useState } from "react";
import { CheckCircle, X, ExternalLink } from "lucide-react";
import { deleteMonitor } from "../../api/monitors.js";
import { toast } from "sonner";

/**
 * Renders a single monitor that was just created by the AI assistant.
 * Accept — dismisses the card (monitor stays).
 * Remove — deletes the monitor via the API and dismisses the card.
 */
export default function MonitorProposalCard({ monitor, onDismiss }) {
  const [removing, setRemoving] = useState(false);

  async function handleRemove() {
    setRemoving(true);
    try {
      await deleteMonitor(monitor.id);
      toast.success(`Removed monitor "${monitor.name}"`);
      onDismiss(monitor.id);
    } catch {
      toast.error("Failed to remove monitor");
      setRemoving(false);
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{monitor.name}</p>
          <p className="mt-0.5 truncate text-xs text-gray-500">{monitor.target}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
              {monitor.type}
            </span>
            <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
              {monitor.interval_seconds}s
            </span>
            {(monitor.locations || []).map((loc) => (
              <span
                key={loc}
                className="inline-flex items-center rounded bg-teal-50 px-1.5 py-0.5 text-xs font-medium text-teal-700"
              >
                {loc}
              </span>
            ))}
          </div>
        </div>
        <a
          href={`/monitors/${monitor.id}`}
          className="ml-1 flex-shrink-0 text-gray-400 hover:text-teal-600"
          title="Open monitor"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => onDismiss(monitor.id)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50"
        >
          <CheckCircle className="h-3.5 w-3.5" />
          Accept
        </button>
        <button
          onClick={handleRemove}
          disabled={removing}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          {removing ? "Removing…" : "Remove"}
        </button>
      </div>
    </div>
  );
}
